// Third party
import React, { useState } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { connect } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import { Typography } from '@capintel/component-lib/typography';
// Actions
import { markAnnouncementsAsRead } from '@/redux/modules/announcements/user';

// Types
import { Announcement, UserAnnouncement, AnnouncementBody } from '@/redux/modules/types/announcements';
import Locale from '@/types/locale';

// Components
import { PrimaryLink } from '@/views/components/buttons';
import { Tooltip } from '@/views/components/tooltips';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '@/utils/i18n';

type Props = {
  item: Announcement | UserAnnouncement;
  locale?: Locale;
  totalText?: boolean;
  onReadMore?: () => void;
};

const NotificationBody = (props: Props) => {
  const { item, locale, totalText, onReadMore } = props;
  const { body } = item;
  const [showMore, setShowMore] = useState<boolean>(false);
  const [queueRead, setQueueRead] = useState<string[]>([]);
  const itemRead = 'read' in item ? item.read : false;
  const maxItemsToDisplay = 3;
  const maxTextLength = 128;

  const queueForRead = (id: string) => {
    if (!queueRead.includes(id)) {
      setQueueRead((prevState: string[]) => [...prevState, id]);
    }
    markAsRead();
  };

  const markAsRead = debounce(() => markAnnouncementsAsRead(queueRead), 500);

  const downloadAll = () => {
    const items = (item.body as Array<AnnouncementBody>) || [];
    return items.map((item: AnnouncementBody) => window.open(item?.link, '_blank'));
  };

  const renderBody = () => {
    if (!body) return null;

    if (!Array.isArray(body)) {
      const localizedString = formatLocalizedString(body, locale) || '';
      const text =
        !totalText && localizedString.length > maxTextLength ? (
          <>
            {localizedString.slice(0, maxTextLength)}...
            <PrimaryLink onClick={onReadMore}>
              <FormattedMessage id="AnnouncementsAdvisor.ReadMore" />
            </PrimaryLink>
          </>
        ) : (
          localizedString
        );

      return <Typography variant="bodyMdRegular">{text}</Typography>;
    }

    return (
      <>
        <ul className="notification-body-items">
          {body.map((body: AnnouncementBody, index: number) => (
            <VisibilitySensor key={index} onChange={isVisible => isVisible && !itemRead && queueForRead(item._id)}>
              <li className={classNames({ hidden: index > maxItemsToDisplay - 1 && !showMore })}>
                <div className="notification-body-item">
                  <div className="symbol">{body.symbol || ''}</div>
                  <div className="name">{body.name || ''}</div>
                  {body.link && (
                    <Tooltip message={formatMessage('Notification.MaterialChange.DownloadReport')} place="bottom">
                      <i className="icon-download" onClick={() => window.open(body.link, '_blank')} />
                    </Tooltip>
                  )}
                </div>
              </li>
            </VisibilitySensor>
          ))}
        </ul>

        {body.length > maxItemsToDisplay && (
          <div className="show-more" onClick={() => setShowMore(!showMore)}>
            <FormattedMessage id={`Notification.MaterialChange.${showMore ? 'ShowLess' : 'ShowMore'}`} />
          </div>
        )}

        {body.length > 1 && (
          <Tooltip message={formatMessage('Notification.MaterialChange.PopupBlocker')}>
            <div className="download-all" onClick={downloadAll}>
              <FormattedMessage id="Notification.MaterialChange.DownloadAll" />
            </div>
          </Tooltip>
        )}
      </>
    );
  };

  return <div className="notification-body">{renderBody()}</div>;
};

export default connect(null, {
  markAnnouncementsAsRead,
})(NotificationBody);
